import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useStore } from './store/useStore';

/* ═══════════════════════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════════════════════ */

const QUOTES = [
  "Some nights don't need answers. Just music.",
  "The city sleeps, but the silence stays awake.",
  "In the quiet, you find the loudest truths.",
  "Rain is just the sky trying to reach you.",
  "Between midnight and morning — that's where I live.",
  "Some sounds are felt, not heard.",
  "The best conversations happen in silence.",
  "Not all who wander are lost. Some are just listening.",
  "Let the rain speak. It knows what you feel.",
  "Every heartbeat is a rhythm waiting to be found.",
  "Close your eyes. The music is already playing.",
  "The night doesn't judge. Neither does my sound.",
];

const TRACKS = [
  { id: 1, title: 'Midnight Rain', duration: '3:42', year: '2024', gradient: 'from-indigo-900 via-purple-900 to-violet-800', cover: '/covers/midnight-rain.jpg', links: { spotify: 'https://open.spotify.com', appleMusic: 'https://music.apple.com', youtubeMusic: 'https://music.youtube.com', soundcloud: 'https://soundcloud.com', deezer: 'https://www.deezer.com', tidal: 'https://tidal.com' } },
  { id: 2, title: 'Neon Solitude', duration: '4:15', year: '2024', gradient: 'from-violet-900 via-fuchsia-900 to-purple-900', cover: '/covers/neon-solitude.jpg', links: { spotify: 'https://open.spotify.com', appleMusic: 'https://music.apple.com', youtubeMusic: 'https://music.youtube.com', soundcloud: 'https://soundcloud.com', deezer: 'https://www.deezer.com', tidal: 'https://tidal.com' } },
  { id: 3, title: '3 AM Thoughts', duration: '3:58', year: '2023', gradient: 'from-slate-900 via-blue-900 to-indigo-900', cover: '/covers/3am-thoughts.jpg', links: { spotify: 'https://open.spotify.com', appleMusic: 'https://music.apple.com', youtubeMusic: 'https://music.youtube.com', soundcloud: 'https://soundcloud.com', deezer: 'https://www.deezer.com', tidal: 'https://tidal.com' } },
  { id: 4, title: 'Window Seat', duration: '5:01', year: '2023', gradient: 'from-gray-900 via-purple-900 to-slate-800', cover: '/covers/window-seat.jpg', links: { spotify: 'https://open.spotify.com', appleMusic: 'https://music.apple.com', youtubeMusic: 'https://music.youtube.com', soundcloud: 'https://soundcloud.com', deezer: 'https://www.deezer.com', tidal: 'https://tidal.com' } },
  { id: 5, title: 'City Lights & You', duration: '4:33', year: '2023', gradient: 'from-blue-950 via-purple-950 to-indigo-900', cover: '/covers/city-lights.jpg', links: { spotify: 'https://open.spotify.com', appleMusic: 'https://music.apple.com', youtubeMusic: 'https://music.youtube.com', soundcloud: 'https://soundcloud.com', deezer: 'https://www.deezer.com', tidal: 'https://tidal.com' } },
  { id: 6, title: 'Drowning Quietly', duration: '3:27', year: '2022', gradient: 'from-stone-900 via-zinc-800 to-neutral-900', cover: '/covers/drowning-quietly.jpg', links: { spotify: 'https://open.spotify.com', appleMusic: 'https://music.apple.com', youtubeMusic: 'https://music.youtube.com', soundcloud: 'https://soundcloud.com', deezer: 'https://www.deezer.com', tidal: 'https://tidal.com' } },
];

const STREAMING_PLATFORMS = [
  { key: 'spotify' as const, name: 'Spotify', color: '#1DB954' },
  { key: 'appleMusic' as const, name: 'Apple Music', color: '#FC3C44' },
  { key: 'youtubeMusic' as const, name: 'YouTube Music', color: '#FF0000' },
  { key: 'soundcloud' as const, name: 'SoundCloud', color: '#FF5500' },
  { key: 'deezer' as const, name: 'Deezer', color: '#A238FF' },
  { key: 'tidal' as const, name: 'Tidal', color: '#00FFFF' },
] as const;

function StreamingIcon({ platform }: { platform: typeof STREAMING_PLATFORMS[number] }) {
  switch (platform.key) {
    case 'spotify':
      return (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
        </svg>
      );
    case 'appleMusic':
      return (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043A5.022 5.022 0 0019.7.165a10.16 10.16 0 00-1.564-.073C17.342.03 16.548.016 15.754.009 14.953.002 14.151 0 13.35 0H10.65c-.801 0-1.603.002-2.404.009-.794.007-1.588.021-2.382.083-.529.042-1.053.11-1.564.226A5.022 5.022 0 002.425.891C1.308 1.624.562 2.624.246 3.934A9.23 9.23 0 00.006 6.124C-.002 6.698 0 7.273 0 7.847v8.305c0 .575-.002 1.15.006 1.724a9.23 9.23 0 00.24 2.19c.317 1.31 1.062 2.31 2.18 3.043a5.022 5.022 0 001.875.726c.51.116 1.035.184 1.564.226.794.062 1.588.076 2.382.083.801.007 1.603.009 2.404.009h2.7c.801 0 1.603-.002 2.404-.009.794-.007 1.588-.021 2.382-.083.529-.042 1.053-.11 1.564-.226a5.022 5.022 0 001.875-.726c1.118-.733 1.863-1.733 2.18-3.043.153-.71.218-1.435.24-2.19.008-.575.006-1.15.006-1.724V7.847c0-.575.002-1.15-.006-1.724zM16.949 17.6H14.93c-.076 0-.107-.038-.135-.1l-1.34-3.126H9.546l-1.28 3.084c-.034.084-.076.142-.174.142H6.157c-.098 0-.12-.05-.1-.142l4.875-11.607c.038-.09.084-.134.174-.134h1.01c.09 0 .134.044.172.134l4.869 11.609c.02.09-.002.14-.108.14z"/>
        </svg>
      );
    case 'youtubeMusic':
      return (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228 18.228 15.432 18.228 12 15.432 5.772 12 5.772zM9.684 15.54V8.46L15.816 12l-6.132 3.54z"/>
        </svg>
      );
    case 'soundcloud':
      return (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.05-.1-.1-.1zm-.899.828c-.06 0-.091.037-.104.094L0 14.479l.172 1.308c.013.06.045.094.104.094.057 0 .09-.037.104-.094l.199-1.308-.2-1.332c-.014-.057-.047-.094-.104-.094zm1.834-.642c-.065 0-.107.053-.112.11l-.214 1.963.214 1.932c.005.063.047.11.112.11.063 0 .105-.047.117-.11l.24-1.932-.24-1.963c-.012-.06-.054-.11-.117-.11zm.927-.44c-.073 0-.12.058-.127.125l-.198 1.812.198 1.785c.007.068.054.125.127.125.07 0 .117-.057.128-.125l.225-1.785-.225-1.812c-.011-.07-.058-.125-.128-.125zm.962-.09c-.08 0-.131.065-.136.138l-.182 1.702.182 1.683c.005.073.056.138.136.138.078 0 .129-.065.139-.138l.205-1.683-.205-1.702c-.01-.073-.061-.138-.139-.138zm.985-.368c-.089 0-.142.069-.147.15l-.168 1.52.168 1.488c.005.082.058.15.147.15.086 0 .139-.068.152-.15l.19-1.488-.19-1.52c-.013-.081-.066-.15-.152-.15zm1.009-.123c-.097 0-.153.074-.159.161l-.153 1.643.153 1.6c.006.089.062.161.159.161.094 0 .15-.072.164-.161l.173-1.6-.173-1.643c-.014-.087-.07-.161-.164-.161zm1.078.033c-.104 0-.164.082-.168.176l-.14 1.434.14 1.408c.004.096.064.176.168.176.101 0 .161-.08.175-.176l.157-1.408-.157-1.434c-.014-.094-.074-.176-.175-.176zm2.075-1.716c-.096-.049-.199-.075-.308-.075-.102 0-.203.026-.297.075-.13.065-.226.185-.282.322l-.348 1.869-.015 1.364.131.701.287 1.321c.055.136.151.256.282.321.093.049.195.075.296.075.109 0 .212-.026.308-.075.131-.065.227-.185.283-.321l.319-1.321.068-.701-.012-1.364-.359-1.869c-.056-.137-.152-.257-.283-.322-.049-.026-.104-.039-.16-.049l-.248.003zM9.394 10.69c-.058 0-.112.015-.165.042-.13.068-.222.192-.273.327l-.295 1.738-.009 1.223.104.67.243 1.228c.051.135.143.259.273.327.053.027.107.042.165.042.063 0 .12-.015.174-.042.13-.068.222-.192.273-.327l.276-1.228.061-.67-.009-1.223-.331-1.738c-.051-.135-.143-.259-.273-.327-.054-.027-.111-.042-.174-.042zm1.173.864c-.073 0-.14.02-.201.057-.134.077-.23.211-.284.357l-.265 1.277-.007.867.093.597.219 1.047c.054.146.15.28.284.357.061.037.128.057.201.057.079 0 .151-.02.213-.057.134-.077.23-.211.284-.357l.249-1.047.055-.597-.007-.867-.311-1.277c-.054-.146-.15-.28-.284-.357-.062-.037-.134-.057-.213-.057zm1.312-.173c-.008-.01-.02-.015-.031-.02l-.077-.009c-.085 0-.163.023-.234.067-.14.085-.241.231-.298.394l-.243 1.145-.005.791.086.534.2.945c.057.163.158.309.298.394.071.044.149.067.234.067.091 0 .173-.023.244-.067.14-.085.241-.231.298-.394l.23-.945.047-.534-.005-.791-.283-1.145c-.057-.163-.158-.309-.298-.394l-.077-.042-.077.028zm1.476.354c-.148.091-.256.248-.316.423l-.228 1.086.22 1.033c.06.175.168.332.316.423.076.047.158.071.245.071.093 0 .178-.024.254-.071.148-.091.256-.248.316-.423l.257-1.033-.243-1.086c-.06-.175-.168-.332-.316-.423-.076-.047-.158-.071-.254-.071-.087 0-.169.024-.245.071h-.076zm8.732 2.741c.209 0 .37-.049.492-.156.122-.107.19-.264.209-.481l.026-.471-.026-.476c-.019-.213-.087-.37-.209-.477-.122-.107-.283-.158-.492-.158-.205 0-.368.051-.49.158-.122.107-.19.264-.209.477l-.026.476.026.471c.019.217.087.374.209.481.122.107.285.156.49.156zm-2.266.253c.227 0 .404-.053.535-.163.131-.111.209-.276.236-.504l.031-.512-.031-.519c-.027-.225-.105-.39-.236-.501-.131-.111-.308-.164-.535-.164-.225 0-.404.053-.535.164-.131.111-.209.276-.236.501l-.031.519.031.512c.027.228.105.393.236.504.131.11.31.163.535.163z"/>
        </svg>
      );
    case 'deezer':
      return (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M18.81 4.16v3.03H24V4.16h-5.19zM6.27 8.38v3.027h5.19V8.38H6.27zm12.54 0v3.027H24V8.38h-5.19zM6.27 12.594v3.027h5.19v-3.027H6.27zm6.27 0v3.027h5.19v-3.027h-5.19zm6.27 0v3.027H24v-3.027h-5.19zM0 16.81v3.029h5.19v-3.03H0zm6.27 0v3.029h5.19v-3.03H6.27zm6.27 0v3.029h5.19v-3.03h-5.19zm6.27 0v3.029H24v-3.03h-5.19z"/>
        </svg>
      );
    case 'tidal':
      return (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M12.012 3.992L8.008 7.996 4.004 3.992 0 7.996 4.004 12 8.008 7.996 12.012 12 16.016 7.996 12.012 3.992zm0 8.008l-4.004 4.004L4.004 12 0 15.992 4.004 20l4.004-4.004L12.012 20l4.004-4.004L12.012 12z"/>
        </svg>
      );
    default:
      return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   RAIN EFFECT
   ═══════════════════════════════════════════════════════════════ */

function RainEffect({ count = 80, opacity = 0.6 }: { count?: number; opacity?: number }) {
  const drops = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      height: `${12 + Math.random() * 25}px`,
      delay: `${Math.random() * 4}s`,
      duration: `${0.6 + Math.random() * 0.8}s`,
      opacity: `${0.15 + Math.random() * opacity * 0.5}`,
    }));
  }, [count, opacity]);

  return (
    <div className="rain-container">
      {drops.map((drop) => (
        <div
          key={drop.id}
          className="raindrop"
          style={{
            left: drop.left,
            height: drop.height,
            animationDelay: drop.delay,
            animationDuration: drop.duration,
            opacity: drop.opacity,
          }}
        />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   GRAIN OVERLAY
   ═══════════════════════════════════════════════════════════════ */

function GrainOverlay() {
  return (
    <div className="grain-overlay">
      <svg width="100%" height="100%">
        <filter id="grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#grain)" />
      </svg>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   DYNAMIC QUOTE
   ═══════════════════════════════════════════════════════════════ */

function DynamicQuote() {
  const quote = useMemo(() => QUOTES[Math.floor(Math.random() * QUOTES.length)], []);

  return (
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 2, delay: 1.5, ease: 'easeOut' }}
      className="text-body text-sm md:text-base tracking-widest uppercase"
      style={{ color: 'var(--color-faint-white)' }}
    >
      {quote}
    </motion.p>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HERO SECTION
   ═══════════════════════════════════════════════════════════════ */

function HeroSection() {
  const { hasEntered, setEntered } = useStore();
  const heroRef = useRef<HTMLDivElement>(null);

  const handleEnter = () => {
    setEntered(true);
    setTimeout(() => {
      document.getElementById('music-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  };

  return (
    <section
      ref={heroRef}
      className={`relative min-h-screen flex items-center justify-center overflow-hidden ${hasEntered ? 'hero-entered' : ''}`}
    >
      {/* Background Image */}
      <div
        className="hero-bg-image"
        style={{
          backgroundImage: `url('https://public.youware.com/users-website-assets/prod/4a4841f6-3add-4ed2-8ae1-7371c8f96efd/eac6360249d2441b892995db178e5cc1.jpg')`,
        }}
      />
      {/* Gradient Overlays */}
      <div className="hero-gradient-overlay" />
      {/* Rain */}
      <RainEffect count={100} opacity={0.7} />
      {/* Ambient Glows */}
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.06) 0%, transparent 70%)',
          animation: 'breathe 6s ease-in-out infinite',
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(59, 7, 100, 0.08) 0%, transparent 70%)',
          animation: 'breathe 8s ease-in-out infinite reverse',
        }}
      />
      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Pre-title */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, delay: 0.5 }}
          className="mb-8"
        >
          <div className="neon-line w-20 mx-auto mb-8" />
        </motion.div>

        {/* Main Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.8, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mb-6 flex justify-center"
        >
          <img src="/logo.png" alt="Aiden Waves" className="max-h-40 sm:max-h-48 md:max-h-56 lg:max-h-64 xl:max-h-72 w-auto object-contain" />
        </motion.div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, delay: 1.2 }}
          className="text-body font-light text-base sm:text-lg md:text-xl tracking-[0.25em] uppercase mb-12"
          style={{ color: 'var(--color-dim-white)' }}
        >
          Soundtracks for late nights & deep thoughts.
        </motion.p>

        {/* Dynamic Quote */}
        <div className="mb-14">
          <DynamicQuote />
        </div>

        {/* CTA Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 2 }}
          whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(168, 85, 247, 0.3)' }}
          whileTap={{ scale: 0.97 }}
          onClick={handleEnter}
          className="group relative px-10 py-4 border border-white/15 rounded-full text-body text-sm tracking-[0.3em] uppercase overflow-hidden cursor-pointer transition-all duration-500"
          style={{
            color: 'var(--color-soft-white)',
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <span className="relative z-10 transition-colors duration-500 group-hover:text-white">Stay HerE</span>
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(59, 7, 100, 0.15) 100%)',
            }}
          />
        </motion.button>

        {/* Scroll indicator */}
        <AnimatePresence>
          {hasEntered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="mt-16"
            >
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="flex flex-col items-center gap-2"
              >
                <span className="text-body text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--color-faint-white)' }}>
                  Scroll
                </span>
                <svg width="16" height="24" viewBox="0 0 16 24" fill="none" className="opacity-30">
                  <path d="M8 4L8 20M8 20L2 14M8 20L14 14" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SECTION HEADER
   ═══════════════════════════════════════════════════════════════ */

function SectionHeader({ number, title }: { number: string; title: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 1, ease: 'easeOut' }}
      className="mb-16 md:mb-20"
    >
      <span
        className="text-body text-xs tracking-[0.4em] uppercase block mb-3"
        style={{ color: 'var(--color-neon-purple)' }}
      >
        {number}
      </span>
      <h2
        className="text-display text-3xl sm:text-4xl md:text-5xl font-light tracking-[0.15em]"
        style={{ color: 'var(--color-soft-white)' }}
      >
        {title}
      </h2>
      <div className="neon-line w-16 mt-6" />
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MUSIC SECTION
   ═══════════════════════════════════════════════════════════════ */

function StreamingMenu({ track, onClose }: { track: typeof TRACKS[0]; onClose: () => void }) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  return (
    <motion.div
      ref={menuRef}
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 10 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="absolute z-50 left-0 right-0 top-full mt-2 mx-4 md:mx-0 rounded-xl overflow-hidden"
      style={{
        background: 'rgba(10, 10, 46, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(168, 85, 247, 0.15)',
        boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 30px rgba(168, 85, 247, 0.1)',
      }}
    >
      <div className="px-4 py-3 border-b border-white/5">
        <p className="text-body text-xs tracking-widest uppercase" style={{ color: 'var(--color-faint-white)' }}>
          Ouvir em
        </p>
      </div>
      <div className="py-2">
        {STREAMING_PLATFORMS.map((platform) => {
          const url = track.links[platform.key];
          if (!url) return null;
          return (
            <a
              key={platform.key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-2.5 transition-all duration-300 hover:bg-white/5 group/stream"
              style={{ color: 'var(--color-soft-white)' }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover/stream:scale-110"
                style={{ background: `${platform.color}20`, color: platform.color }}
              >
                <StreamingIcon platform={platform} />
              </div>
              <span className="text-body text-sm tracking-wider">{platform.name}</span>
              <svg className="ml-auto w-3 h-3 opacity-0 group-hover/stream:opacity-50 transition-opacity" viewBox="0 0 12 12" fill="none">
                <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          );
        })}
      </div>
    </motion.div>
  );
}

function MusicSection() {
  const [activeTrack, setActiveTrack] = useState<number | null>(null);

  return (
    <section id="music-section" className="relative py-24 md:py-32 px-6 section-dark">
      {/* Subtle background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url('https://public.youware.com/users-website-assets/prod/4a4841f6-3add-4ed2-8ae1-7371c8f96efd/81e93e6933014a388d98e3cb6db53b5d.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          filter: 'brightness(0.15) saturate(0.5)',
          opacity: 0.4,
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto">
        <SectionHeader number="01 — Discography" title="Latest Releases" />

        {/* Track Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-20">
          {TRACKS.map((track, index) => (
            <motion.div
              key={track.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.8, delay: index * 0.1, ease: 'easeOut' }}
              className="track-card group relative"
            >
              {/* Cover Art */}
              <div
                className={`relative aspect-square bg-gradient-to-br ${track.gradient} overflow-hidden cursor-pointer rounded-t-xl`}
                onClick={() => setActiveTrack(activeTrack === track.id ? null : track.id)}
              >
                {/* Cover Image */}
                <img
                  src={track.cover}
                  alt={track.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />

                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                {/* Track Number */}
                <div className="absolute top-4 left-4">
                  <span className="text-body text-xs tracking-widest" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {String(track.id).padStart(2, '0')}
                  </span>
                </div>

                {/* Play/Listen Overlay */}
                <div className="track-play-overlay rounded-none">
                  <div className="w-14 h-14 rounded-full border border-white/30 flex items-center justify-center backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M6 4L16 10L6 16V4Z" fill="white" fillOpacity="0.8" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Streaming Menu */}
              <AnimatePresence>
                {activeTrack === track.id && (
                  <StreamingMenu
                    track={track}
                    onClose={() => setActiveTrack(null)}
                  />
                )}
              </AnimatePresence>

              {/* Track Info */}
              <div className="p-5 rounded-b-xl">
                <h3 className="text-body font-medium text-sm tracking-wider mb-2" style={{ color: 'var(--color-soft-white)' }}>
                  {track.title}
                </h3>
                <div className="flex justify-between items-center">
                  <span className="text-body text-xs tracking-wider" style={{ color: 'var(--color-faint-white)' }}>
                    {track.year}
                  </span>
                  <span className="text-body text-xs tracking-wider" style={{ color: 'var(--color-faint-white)' }}>
                    {track.duration}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Spotify Embed */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 1, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <div className="text-center mb-8">
            <span
              className="text-body text-xs tracking-[0.3em] uppercase"
              style={{ color: 'var(--color-faint-white)' }}
            >
              Now Playing
            </span>
          </div>
          <div className="spotify-embed-container">
            <iframe
              src="https://open.spotify.com/embed/playlist/37i9dQZF1DX3Ogo9pFvBkY?utm_source=generator&theme=0"
              width="100%"
              height="352"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              style={{ borderRadius: '12px' }}
              title="Aiden Waves - Spotify Playlist"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   EXPERIENCE / FOCUS MODE SECTION
   ═══════════════════════════════════════════════════════════════ */

function ExperienceSection() {
  const { isFocusMode, isRainActive, toggleFocusMode, toggleRain } = useStore();
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Clock update
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Rain sound
  const startRainSound = useCallback(() => {
    if (audioContextRef.current) return;

    const ctx = new AudioContext();
    audioContextRef.current = ctx;

    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 800;
    filter.Q.value = 0.5;

    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 2);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    source.start();

    gainNodeRef.current = gain;
  }, []);

  const stopRainSound = useCallback(() => {
    if (gainNodeRef.current && audioContextRef.current) {
      gainNodeRef.current.gain.linearRampToValueAtTime(0, audioContextRef.current.currentTime + 1);
      setTimeout(() => {
        audioContextRef.current?.close();
        audioContextRef.current = null;
        gainNodeRef.current = null;
      }, 1200);
    }
  }, []);

  const handleToggleRain = () => {
    toggleRain();
    if (!isRainActive) {
      startRainSound();
    } else {
      stopRainSound();
    }
  };

  const timeStr = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      {/* Focus Mode Overlay */}
      <AnimatePresence>
        {isFocusMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="focus-mode-container"
          >
            {/* Rain in focus mode */}
            <RainEffect count={120} opacity={isRainActive ? 0.9 : 0.4} />

            {/* Ambient glow */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at 50% 50%, rgba(168, 85, 247, 0.04) 0%, transparent 60%)',
              }}
            />

            {/* Clock */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, delay: 0.5 }}
              className="text-center mb-12"
            >
              <div
                className="text-display text-6xl sm:text-7xl md:text-8xl font-light tracking-[0.1em] text-glow mb-4"
                style={{ color: 'var(--color-soft-white)' }}
              >
                {timeStr}
              </div>
              <div className="neon-line w-12 mx-auto" />
            </motion.div>

            {/* Focus message */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
              className="text-body text-sm tracking-[0.3em] uppercase mb-16"
              style={{ color: 'var(--color-faint-white)' }}
            >
              Focus. Breathe. Listen.
            </motion.p>

            {/* Controls */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.3 }}
              className="flex items-center gap-6"
            >
              {/* Rain toggle */}
              <button
                onClick={handleToggleRain}
                className="group flex flex-col items-center gap-2 cursor-pointer"
              >
                <div
                  className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-500 ${
                    isRainActive ? 'border-purple-500/40' : 'border-white/10'
                  }`}
                  style={{
                    background: isRainActive ? 'rgba(168, 85, 247, 0.1)' : 'rgba(255,255,255,0.03)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={isRainActive ? '#a855f7' : 'rgba(255,255,255,0.4)'}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  >
                    <path d="M12 2v6M6 8l2-2M18 8l-2-2M8 14a4 4 0 108 0c0-3-4-5-4-5s-4 2-4 5z" />
                  </svg>
                </div>
                <span className="text-body text-[10px] tracking-[0.2em] uppercase" style={{ color: 'var(--color-faint-white)' }}>
                  Rain
                </span>
              </button>

              {/* Exit button */}
              <button
                onClick={toggleFocusMode}
                className="px-8 py-3 border border-white/10 rounded-full text-body text-xs tracking-[0.25em] uppercase cursor-pointer transition-all duration-500 hover:border-white/25 hover:bg-white/5"
                style={{ color: 'var(--color-faint-white)', background: 'rgba(255,255,255,0.02)' }}
              >
                Exit Focus
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Experience Section (visible when not in focus mode) */}
      <section className="relative py-24 md:py-32 px-6 section-darker">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <SectionHeader number="02 — Experience" title="Focus Mode" />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-body font-light text-base md:text-lg leading-relaxed mb-12 max-w-2xl mx-auto"
            style={{ color: 'var(--color-dim-white)' }}
          >
            Strip everything away. Just you, the music, and the sound of rain.
            A minimal space designed for deep work, reflection, or simply being.
          </motion.p>

          {/* Feature pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {['Ambient Rain Sounds', 'Minimal Interface', 'Music Continues', 'No Distractions'].map((feature) => (
              <span
                key={feature}
                className="px-5 py-2 rounded-full border text-body text-xs tracking-wider"
                style={{
                  borderColor: 'rgba(255,255,255,0.08)',
                  color: 'var(--color-faint-white)',
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                {feature}
              </span>
            ))}
          </motion.div>

          {/* Activate Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 0 60px rgba(168, 85, 247, 0.25)' }}
              whileTap={{ scale: 0.97 }}
              onClick={toggleFocusMode}
              className="px-12 py-4 rounded-full text-body text-sm tracking-[0.3em] uppercase cursor-pointer transition-all duration-500"
              style={{
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(59, 7, 100, 0.2) 100%)',
                border: '1px solid rgba(168, 85, 247, 0.25)',
                color: 'var(--color-soft-white)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 0 30px rgba(168, 85, 247, 0.1)',
              }}
            >
              Activate Focus Mode
            </motion.button>
          </motion.div>
        </div>
      </section>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ABOUT SECTION
   ═══════════════════════════════════════════════════════════════ */

function AboutSection() {
  return (
    <section className="relative py-24 md:py-36 px-6 section-dark overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url('https://public.youware.com/users-website-assets/prod/4a4841f6-3add-4ed2-8ae1-7371c8f96efd/fb3d81a6dd71435ea84b90751ecf3231.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.2) saturate(0.3)',
          opacity: 0.3,
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto">
        <SectionHeader number="03 — About" title="The Story" />

        <div className="space-y-8 md:space-y-12">
          {[
            "I make music for the hours that don't have names.",
            "For the windows that hold rain and wondering. For the thoughts that only arrive at 3 AM — when the world is finally quiet enough to listen.",
            "I don't write songs. I build rooms. And in each room, there's a window, a chair, a warm light, and the sound of rain falling somewhere far enough to feel like a memory.",
            "Walk in. Stay as long as you need. The door doesn't close behind you — it just waits until you're ready to leave.",
            "This is Aiden Waves. And these are the sounds between your thoughts.",
          ].map((paragraph, index) => (
            <motion.p
              key={index}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 1, delay: index * 0.15, ease: 'easeOut' }}
              className={`text-display font-light leading-relaxed ${
                index === 0 || index === 4
                  ? 'text-xl sm:text-2xl md:text-3xl tracking-wide'
                  : 'text-base sm:text-lg md:text-xl'
              }`}
              style={{
                color: index === 0 || index === 4
                  ? 'var(--color-soft-white)'
                  : 'var(--color-dim-white)',
              }}
            >
              {paragraph}
            </motion.p>
          ))}
        </div>

        {/* Signature */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, delay: 0.8 }}
          className="mt-16 md:mt-20"
        >
          <div className="neon-line w-12 mb-8" />
          <p
            className="text-display text-lg md:text-xl italic tracking-wider"
            style={{ color: 'rgba(168, 85, 247, 0.6)' }}
          >
            — Aiden Waves
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════════════════════════════ */

function Footer() {
  return (
    <footer className="relative py-16 md:py-20 px-6">
      <div className="max-w-4xl mx-auto text-center">
        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex justify-center gap-8 mb-12"
        >
          {/* TikTok */}
          <a
            href="https://tiktok.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 transition-all duration-300"
            style={{ color: 'var(--color-faint-white)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#00f2ea')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-faint-white)')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.92 2.92 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 3 15.57 6.33 6.33 0 0 0 9.37 22a6.33 6.33 0 0 0 6.36-6.22V8.79a8.18 8.18 0 0 0 4.77 1.52V6.84a4.84 4.84 0 0 1-1-.15z"/>
            </svg>
            <span className="text-body text-xs tracking-[0.2em] uppercase">TikTok</span>
          </a>

          {/* Instagram */}
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 transition-all duration-300"
            style={{ color: 'var(--color-faint-white)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#E4405F')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-faint-white)')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
            </svg>
            <span className="text-body text-xs tracking-[0.2em] uppercase">Instagram</span>
          </a>
        </motion.div>

        {/* Divider */}
        <div className="neon-line w-16 mx-auto mb-10" />

        {/* Copyright */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.3 }}
          className="space-y-4"
        >
          <div className="flex justify-center">
            <img src="/logo.png" alt="Aiden Waves" className="h-32 w-auto object-contain" />
          </div>
          <p
            className="text-body text-xs tracking-wider"
            style={{ color: 'var(--color-faint-white)' }}
          >
            Made with silence and sound.
          </p>
          <p
            className="text-body text-xs tracking-wider"
            style={{ color: 'rgba(255,255,255,0.15)' }}
          >
            © {new Date().getFullYear()} Aiden Waves. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PARALLAX DIVIDER
   ═══════════════════════════════════════════════════════════════ */

function ParallaxDivider() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 0.5, 0.5, 0]);

  return (
    <div ref={ref} className="relative h-40 md:h-56 flex items-center justify-center overflow-hidden">
      <motion.div style={{ y }} className="text-center">
        <motion.div style={{ opacity }}>
          <div className="neon-line w-24 mx-auto" />
          <div className="mt-4 flex items-center gap-4 justify-center">
            <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(168, 85, 247, 0.4)' }} />
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(168, 85, 247, 0.3)' }} />
            <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(168, 85, 247, 0.4)' }} />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FLOATING AMBIENT ORB
   ═══════════════════════════════════════════════════════════════ */

function AmbientOrb({ style }: { style?: React.CSSProperties }) {
  return (
    <div
      className="absolute rounded-full pointer-events-none"
      style={{
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(168, 85, 247, 0.04) 0%, transparent 70%)',
        filter: 'blur(40px)',
        animation: 'breathe 8s ease-in-out infinite',
        ...style,
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════
   APP
   ═══════════════════════════════════════════════════════════════ */

export default function App() {
  return (
    <div className="relative" style={{ background: 'var(--color-deep-black)' }}>
      {/* Grain overlay on everything */}
      <GrainOverlay />

      {/* Hero */}
      <HeroSection />

      {/* Main Content */}
      <main>
        <ParallaxDivider />
        <MusicSection />
        <ParallaxDivider />
        <ExperienceSection />
        <ParallaxDivider />
        <AboutSection />

        {/* Ambient orbs */}
        <AmbientOrb style={{ top: '40%', left: '-100px', animationDelay: '2s' }} />
        <AmbientOrb style={{ top: '70%', right: '-100px', animationDelay: '4s' }} />

        <Footer />
      </main>
    </div>
  );
}
